import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Group, Stack, Text } from "@mantine/core";
import { IconCheck, IconEdit, IconPlus, IconX } from "@tabler/icons-react";
import { notifyError, notifySuccess } from "../../../utils/notify";
import Logo from "../../common/Logo/Logo";
import ProgressiveWizardShell from "./ProgressiveWizardShell";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import {
  OnboardingProgressResponse,
  UpdateOnboardingProgressRequest,
} from "../types/onboarding";
import { brandColors } from "../../../theme/brandTheme";
import { StaffAvatar } from "../../common/StaffAvatar";
import { StaffFormModal, upsertStaffMember } from "../../forms/StaffForm";
import type { Employee } from "../../../types/salon";
import type {
  SavedStaffMember,
  StaffFormSaveResult,
} from "../../../types/staffForm";
import styles from "./ProgressiveEmployeesWizard.module.css";

type WizardStep = "intro" | "list";

interface ProgressiveEmployeesWizardProps {
  opened: boolean;
  onClose: () => void;
  progress: OnboardingProgressResponse;
  onComplete: (
    postOnboardingProgress: (
      data: UpdateOnboardingProgressRequest,
      salonId?: string,
    ) => Promise<OnboardingProgressResponse>,
  ) => void;
  onSwitchWizard?: (wizard: "employees" | "services" | "payments") => void;
}

/** Which staff member the shared staff form is open for (null employee = new). */
interface StaffFormState {
  opened: boolean;
  employee: Employee | null;
}

const CLOSED_FORM: StaffFormState = { opened: false, employee: null };

/**
 * Onboarding's "Add Employees" step: an intro, then the staff list. Adding or
 * editing a staff member opens the same staff form as Settings → Staff, with
 * its General Info and Hours tabs (services are assigned later).
 */
export default function ProgressiveEmployeesWizard({
  opened,
  onClose,
  progress,
  onComplete,
  onSwitchWizard,
}: ProgressiveEmployeesWizardProps) {
  const { updateOnboardingProgress } = useApp();
  const { employees, businessInfo, refreshEmployees, setEmployees } =
    useSalon() as unknown as {
      employees: Employee[] | null;
      businessInfo: { id: string } | null;
      refreshEmployees: () => Promise<Employee[] | null>;
      setEmployees: React.Dispatch<React.SetStateAction<Employee[] | null>>;
    };

  // Track if we've already fetched data for this modal session
  const hasFetchedRef = useRef(false);
  // The last staff member the form saved, re-merged if a refresh races the write.
  const lastSavedRef = useRef<SavedStaffMember | null>(null);

  const initialStep: WizardStep =
    progress.progressiveEmployees === null ? "intro" : "list";
  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [isLoading, setIsLoading] = useState(false);
  const [staffForm, setStaffForm] = useState<StaffFormState>(CLOSED_FORM);

  // Load staff when the wizard opens, once per open.
  useEffect(() => {
    if (opened && businessInfo?.id && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      refreshEmployees();
    }
    if (!opened) {
      hasFetchedRef.current = false;
    }
  }, [opened, businessInfo?.id]); // Intentionally exclude refreshEmployees to prevent an infinite loop

  const handleIntroNext = useCallback(async () => {
    setIsLoading(true);
    try {
      // Only persist progress when the salon id is known: without the
      // ?salonId= query param the backend silently falls back to a
      // staff-keyed row that can belong to another location.
      if (businessInfo?.id) {
        await updateOnboardingProgress(
          {
            section: "progressive_employees",
            state: "inprogress",
          },
          businessInfo.id,
        );
      }
      setCurrentStep("list");
    } catch (error) {
      notifyError("Failed to update progress. Please try again.", {
        title: "Error",
        icon: <IconX size={18} />,
        withCloseButton: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [updateOnboardingProgress, businessInfo?.id]);

  const handleListNext = useCallback(() => {
    // onComplete handles: optimistic state update, routing decision, and POST completion
    onComplete(updateOnboardingProgress);
  }, [updateOnboardingProgress, onComplete]);

  const openStaffForm = (employee: Employee | null) =>
    setStaffForm({ opened: true, employee });
  const closeStaffForm = () => setStaffForm(CLOSED_FORM);

  const handleStaffSaved = (saved: SavedStaffMember) => {
    lastSavedRef.current = saved;
    setEmployees((current) => upsertStaffMember(current, saved as Employee));
  };

  const handleStaffSaveComplete = async ({
    staffId,
    isCreate,
  }: StaffFormSaveResult) => {
    const saved = lastSavedRef.current;
    const refreshed = await refreshEmployees();
    // If the list came back without the member we just wrote, keep it until
    // the backend catches up.
    if (
      saved &&
      staffId &&
      refreshed &&
      !refreshed.some((employee) => employee.id === staffId)
    ) {
      setEmployees(upsertStaffMember(refreshed, saved as Employee));
    }
    const name = saved?.first_name || "Employee";
    notifySuccess(`${name} has been ${isCreate ? "added" : "updated"}!`, {
      title: "Success",
      icon: <IconCheck size={18} />,
      withCloseButton: true,
    });
    closeStaffForm();
  };

  const renderIntroStep = () => (
    <Stack
      align="center"
      justify="center"
      gap="md"
      className={styles.introContainer}
    >
      <Logo variant="icon" size="lg" />
      <Text size="xl" fw={700} ta="center">
        Add Employees
      </Text>
      <Stack gap="xs" ta="center" className={styles.benefitsList}>
        <Group gap="xs" justify="center">
          <IconCheck size={16} color="#868e96" />
          <Text size="sm" ta="center">
            Organize shifts and availability in one place.
          </Text>
        </Group>
        <Group gap="xs" justify="center">
          <IconCheck size={16} color="#868e96" />
          <Text size="sm" ta="center">
            Control what each team member can access
          </Text>
        </Group>
        <Group gap="xs" justify="center">
          <IconCheck size={16} color="#868e96" />
          <Text size="sm" ta="center">
            View and coordinate appointments across your team
          </Text>
        </Group>
      </Stack>
    </Stack>
  );

  const renderListStep = () => {
    // Filter out the current user (owner) from the display list
    const staffList = employees?.filter((e) => !e.isSelf) || [];
    const ownerEmployee = employees?.find((e) => e.isSelf);

    return (
      <Box className={styles.listContainer}>
        <Group justify="space-between" mb="md">
          <Text size="lg" fw={600}>
            Add Employees
          </Text>
          <Button
            variant="outline"
            leftSection={<IconPlus size={16} color="#5a5a5a" />}
            onClick={() => openStaffForm(null)}
            styles={{
              root: {
                backgroundColor: "#f8f9fa",
                borderColor: "#dedede",
                color: "#5a5a5a",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                  borderColor: "#dedede",
                },
              },
            }}
          >
            Add Employee
          </Button>
        </Group>

        <Text size="sm" c="dimmed" mb="lg">
          Add your team members to help manage bookings and schedules.
        </Text>

        {/* Owner Section */}
        {ownerEmployee && (
          <Group
            justify="space-between"
            align="center"
            className={styles.employeeCard}
          >
            <Group gap="md">
              <StaffAvatar
                photoUrl={
                  ownerEmployee.staff_photo_url ?? ownerEmployee.avatar_url
                }
                firstName={ownerEmployee.first_name}
                lastName={ownerEmployee.last_name}
                size="md"
                radius="xl"
                color="teal"
                style={{ border: "1px solid #e9ecef" }}
              />
              <Stack gap={4} className={styles.employeeInfo}>
                <Text fw={600}>
                  {ownerEmployee.first_name} {ownerEmployee.last_name}
                </Text>
                <Text size="sm" c="dimmed">
                  {ownerEmployee.title || ownerEmployee.role} (You)
                </Text>
              </Stack>
            </Group>
          </Group>
        )}

        {/* Staff List */}
        {staffList.map((employee) => (
          <Group
            key={employee.id}
            justify="space-between"
            align="center"
            className={styles.employeeCard}
          >
            <Group gap="md" style={{ flex: 1 }}>
              <StaffAvatar
                photoUrl={employee.staff_photo_url ?? employee.avatar_url}
                firstName={employee.first_name}
                lastName={employee.last_name}
                size="md"
                radius="xl"
                color="teal"
                style={{ border: "1px solid #e9ecef" }}
              />
              <Stack gap={4} className={styles.employeeInfo}>
                <Text fw={600}>
                  {employee.first_name} {employee.last_name}
                </Text>
                <Text size="sm" c="dimmed">
                  {employee.title || employee.role}
                </Text>
              </Stack>
            </Group>
            <Group gap="xs">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconEdit size={14} />}
                onClick={() => openStaffForm(employee)}
              >
                Edit
              </Button>
            </Group>
          </Group>
        ))}

        {staffList.length === 0 && !ownerEmployee && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            No employees added yet. Click "Add Employee" to get started.
          </Text>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    switch (currentStep) {
      case "intro":
        return renderIntroStep();
      case "list":
        return renderListStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentStep) {
      case "intro":
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              onClick={handleIntroNext}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      case "list":
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => setCurrentStep("intro")}>
              Back
            </Button>
            <Button
              onClick={handleListNext}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ProgressiveWizardShell
        opened={opened}
        onClose={onClose}
        currentStep="employees"
        // Progress dots: intro = dot 1, list = dot 2.
        currentSubStep={currentStep === "intro" ? 1 : 2}
        totalSubSteps={2}
        employeesStatus={progress.progressiveEmployees}
        servicesStatus={progress.progressiveServices}
        paymentsStatus={progress.progressivePayments}
        footer={<Group>{renderFooter()}</Group>}
        onStepClick={onSwitchWizard}
      >
        {renderContent()}
      </ProgressiveWizardShell>

      <StaffFormModal
        opened={staffForm.opened}
        onClose={closeStaffForm}
        title={staffForm.employee ? "Edit Employee" : "Add Employee"}
        showMenu={false}
        initialData={staffForm.employee}
        onStaffSaved={handleStaffSaved}
        onComplete={handleStaffSaveComplete}
      />
    </>
  );
}
